/**
 * The plant picker's three answers (W24 Job B; the owner's rule 1: the backend decides, the app displays): the Plant,
 * Type and Variety drop-downs as the server lists them, one small answer per step the gardener takes, in place of the
 * whole catalogue the app used to download and sort (GET /plants/all and GET /varieties/all, both gone).
 *
 * The server (bloom-backend routes/plantPicker, services/plants/plantPicker) decides the groups and their order, the
 * crops' names and order, the types' labels ("Cherry, bush" where two would read the same) and order, whether Type
 * offers "Not sure" (B52), what Type and Variety start on (B65), the varieties offered (Grow's, the garden owner's own
 * and the acting member's own) and their order, and which variety a name seen in a photo is. Names are in the reader's
 * language (lang). The app draws each answer as sent.
 *
 * Endpoints (behind the garden's gate: 401, 403, 402 as every garden read):
 * - GET /plants/picker/crops?lang= → PickerCrops.
 * - GET /plants/picker/types?cropId=&lang= → PickerTypes (400 for a bad id, 404 for a crop with no type she may use).
 * - GET /plants/picker/varieties?plantId=&lang=&match= → PickerVarieties (400 for a bad id, 404 for a type she may not use).
 *
 * Who uses it: shared/components/plant-fields usePlantPicker (its hooks and query builders).
 *
 * @module api/plantPicker
 */

import { apiClient } from './apiClient';

/** A crop as the Plant drop-down lists it. */
export interface PickerCrop {
  /** The crop: a plant group's id. */
  id: number;
  /** Its name as the drop-down says it ("Tomato"). */
  name: string;
}

/** The kinds the Plant drop-down groups crops under, "mine" (what she grows) first when the garden has any. */
export type PickerGroupKind = 'mine' | 'vegetable' | 'herb' | 'fruit' | 'flower' | 'other';

/** One group of the Plant drop-down. */
export interface PickerCropGroup {
  /** The group: what she grows, or a kind. */
  kind: PickerGroupKind;
  /** Its crops, in the order the drop-down draws them. */
  crops: PickerCrop[];
}

/** The crops answer: the Plant drop-down, in the order it draws. */
export interface PickerCrops {
  /** The groups, in order; a group with no crop is not sent. */
  groups: PickerCropGroup[];
}

/** A type as the Type drop-down lists it. */
export interface PickerType {
  /** The type: a catalogue plant's id. */
  id: number;
  /** Its whole name ("Romaine Lettuce"). */
  name: string;
  /** The Type list's word ("Romaine"; "Cherry, bush" where two types would read the same). */
  label: string;
  /** A type of her own: the list adds "(yours)". */
  own: boolean;
}

/** What Type and Variety start on when a crop is picked (B65, B52), as the server decides it. */
export interface PickerTypesStart {
  /** The type Type starts on, or null. */
  plantId: number | null;
  /** Type starts on "Not sure". */
  typeUnsure: boolean;
  /** The variety Variety starts on, where a type starts; else null. */
  varietyId: number | null;
}

/** The types answer: one crop's Type drop-down. */
export interface PickerTypes {
  /** The crop. */
  crop: PickerCrop;
  /** Its types, in the order the drop-down draws them. */
  types: PickerType[];
  /** The Type list offers "Not sure" first (B52). */
  notSure: boolean;
  /** What Type and Variety start on. */
  start: PickerTypesStart;
}

/** A named variety as the Variety drop-down lists it. */
export interface PickerVariety {
  /** The variety's id. */
  id: number;
  /** Its name. */
  name: string;
  /** One of hers (or the garden owner's): the list adds "(yours)". */
  own: boolean;
}

/** The variety a name seen in a photo is, as the server matched it (this type first, then the crop's others). */
export interface PickerMatch {
  /** The type it is under. */
  plantId: number;
  /** That type's whole name. */
  plantName: string;
  /** The variety. */
  varietyId: number;
  /** Its name as filed. */
  varietyName: string;
}

/** The varieties answer: one type's Variety drop-down, with its type's and crop's names. */
export interface PickerVarieties {
  /** The type. */
  plant: PickerType;
  /** Its crop. */
  crop: PickerCrop;
  /** The type's hidden "Not sure" variety (what "Not sure" saves), or null. */
  notSure: number | null;
  /** The named varieties (never the "Not sure" one), in the order the drop-down draws them. */
  varieties: PickerVariety[];
  /** What Variety starts on. */
  start: { varietyId: number | null };
  /** With a name to match: the variety of that name; else null. */
  matched: PickerMatch | null;
}

/**
 * The Plant drop-down's crops, grouped and ordered by the server.
 *
 * @param lang - The reader's language, for the names.
 * @returns The crops answer.
 */
export async function getPickerCrops(lang: string): Promise<PickerCrops> {
  const response = await apiClient.get<PickerCrops & { success: boolean }>('/plants/picker/crops', { params: { lang } });
  return { groups: response.data.groups };
}

/**
 * One crop's Type drop-down and what it starts on.
 *
 * @param cropId - The crop.
 * @param lang - The reader's language, for the labels.
 * @returns The types answer.
 */
export async function getPickerTypes(cropId: number, lang: string): Promise<PickerTypes> {
  const response = await apiClient.get<PickerTypes & { success: boolean }>('/plants/picker/types', { params: { cropId, lang } });
  const { crop, types, notSure, start } = response.data;
  return { crop, types, notSure, start };
}

/**
 * One type's Variety drop-down, its type's and crop's names, and (with match) which variety a seen name is.
 *
 * @param plantId - The type.
 * @param lang - The reader's language, for the names.
 * @param match - A variety name seen in a photo, to match; absent for none.
 * @returns The varieties answer.
 */
export async function getPickerVarieties(plantId: number, lang: string, match?: string | null): Promise<PickerVarieties> {
  const response = await apiClient.get<PickerVarieties & { success: boolean }>('/plants/picker/varieties', {
    params: { plantId, lang, ...(match ? { match } : {}) },
  });
  const { plant, crop, notSure, varieties, start, matched } = response.data;
  return { plant, crop, notSure, varieties, start, matched: matched ?? null };
}
